import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import "./forgot-password.css";
import { useNavigate } from "react-router";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [apiSuccess, setApiSuccess] = useState(null);
  const emailRef = useRef(null);

  const API_ENDPOINT = "/wp-json/dbsb/v1/forgot-password";

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    validateField(name, value);
  };

  const validateField = (name, value) => {
    const newErrors = { ...errors };
    switch (name) {
      case "email":
        newErrors.email = !value
          ? "Email is required"
          : !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
          ? "Must be a valid email address"
          : "";
        break;
      default:
        break;
    }
    setErrors(newErrors);
  };

  useEffect(() => {
    setTimeout(() => {
      if (emailRef.current) {
        emailRef.current.focus();
      }
    }, 100);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    validateField("email", formData.email);

    if (!formData.email || errors.email) {
      return;
    }

    setIsLoading(true);
    setApiError(null);
    setApiSuccess(null);

    try {
      const response = await fetch(API_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: formData.email,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to send password reset link");
      }

      setApiSuccess("Password reset link sent! Please check your email.");
      // Clear success message after 5 seconds
      setTimeout(() => setApiSuccess(null), 5000);
    } catch (error) {
      setApiError(error.message);
      // Clear error message after 5 seconds
      setTimeout(() => setApiError(null), 5000);
    } finally {
      setIsLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0, y: -50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  const formVariants = {
    hidden: { opacity: 0, x: -100 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: "easeOut", staggerChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: [0.6, -0.05, 0.01, 0.99] },
    },
  };

  const buttonVariants = {
    hover: {
      scale: 1.02,
      backgroundColor: "#d1d9e6",
      transition: { duration: 0.2 },
    },
    tap: { scale: 0.98 },
  };

  return (
    <motion.div
      className="forgot-password-container"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div className="forgot-password-card" variants={containerVariants}>
        <div className="logo">
          <a href="/">
            <img
              src={`${process.env.PUBLIC_URL}/assets/logo.svg`}
              alt="Direct by Syndicate Bio"
            />
          </a>
        </div>
        <motion.div
          className="forgot-password-content"
          variants={formVariants}
          initial="hidden"
          animate="visible"
        >
          <h2>Forgot Your Password?</h2>
          <p>Enter your email to receive a password reset link</p>
          <motion.form
            onSubmit={handleSubmit}
            className="forgot-password-form"
            variants={formVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.div variants={itemVariants} className="form-group">
              <label>Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter your email address"
                ref={emailRef}
              />
              {errors.email && <span className="error">{errors.email}</span>}
            </motion.div>
            {apiError && (
              <motion.p className="error-message" variants={itemVariants}>
                {apiError}
              </motion.p>
            )}
            {apiSuccess && (
              <motion.p className="success-message" variants={itemVariants}>
                {apiSuccess}
              </motion.p>
            )}
            <motion.div variants={itemVariants}>
              <motion.button
                type="submit"
                className={`continue-button ${
                  isLoading || !formData.email || errors.email ? "disabled" : ""
                }`}
                disabled={isLoading || !formData.email || errors.email}
                variants={buttonVariants}
                whileHover="hover"
                whileTap="tap"
              >
                {isLoading ? "Sending..." : "Send Reset Link"}
              </motion.button>
            </motion.div>
          </motion.form>
          <div className="login-section">
            {/* <div className="or-line">
              <ul></ul>
              <p className="login-link">OR</p>
              <ul></ul>
            </div> */}
            <a
              href="/login"
              className="login-link"
              onClick={(e) => {
                e.preventDefault();
                navigate("/login");
              }}
            >
              Back to Login
            </a>
          </div>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export default ForgotPassword;
